import type { Source } from './types';

/**
 * BibTeX formatter for article citations. Used by the "Copy BibTeX" and
 * "Copy all .bib" actions on the article page's citations rail.
 *
 * Minimal, safe format — no LaTeX escaping beyond braces around title
 * (preserves capitalization). Not perfect, but valid for most BibTeX
 * consumers including Zotero/Mendeley import.
 */

function safeBibKey(source: Source): string {
  // cite-key from first author last name + year + pmid/doi tail
  const firstAuthor = (source.authors || '')
    .split(/[,;]/)[0]
    .trim()
    .split(/\s+/)
    .pop() ?? 'Anon';
  const year = (source.year || 'nd').toString().slice(0, 4);
  const tail = (source.pmid || source.doi || source.id || '')
    .toString()
    .replace(/[^a-zA-Z0-9]/g, '')
    .slice(0, 8);
  return `${firstAuthor}${year}${tail ? '_' + tail : ''}`
    .replace(/[^A-Za-z0-9_]/g, '');
}

export function formatBibTeX(source: Source): string {
  const key = safeBibKey(source);
  const fields: string[] = [];
  if (source.title) fields.push(`  title     = {${source.title.replace(/[{}]/g, '')}}`);
  if (source.authors) fields.push(`  author    = {${source.authors}}`);
  if (source.publication) fields.push(`  journal   = {${source.publication}}`);
  if (source.year) fields.push(`  year      = {${source.year}}`);
  if (source.pmid) fields.push(`  pmid      = {${source.pmid}}`);
  if (source.doi) fields.push(`  doi       = {${source.doi}}`);
  if (source.pmid) fields.push(`  url       = {https://pubmed.ncbi.nlm.nih.gov/${source.pmid}/}`);
  else if (source.doi) fields.push(`  url       = {https://doi.org/${source.doi}}`);
  return `@article{${key},\n${fields.join(',\n')}\n}`;
}

export function formatAllBibTeX(sources: Source[]): string {
  return sources.map(formatBibTeX).join('\n\n');
}

export function formatAPA(source: Source): string {
  const authors = source.authors || 'Unknown';
  const year = source.year ? ` (${source.year})` : '';
  const title = source.title ? `. ${source.title}` : '';
  const journal = source.publication ? `. *${source.publication}*` : '';
  const ids = [
    source.doi ? `https://doi.org/${source.doi}` : null,
    source.pmid ? `PMID: ${source.pmid}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return `${authors}${year}${title}${journal}.${ids ? ' ' + ids : ''}`.replace(/\s+\./g, '.');
}
